import React, { useEffect, useMemo, useState } from 'react';

import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Paper,
    Stack,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

import { IconSparkles, IconColumns3, IconUsersGroup } from '@tabler/icons-react';

import { IconArrowsMove, IconEdit, IconPlus, IconSearch, IconTrash, IconArrowLeft } from '@tabler/icons';

import { motion } from 'framer-motion';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import api from '../../../../services/api';
import { useHistory, useParams } from 'react-router-dom';

const MotionCard = motion(Card);

const DEFAULT_PIPELINE = {
    name: '',
    teams_id: ''
};

const DEFAULT_STAGE = {
    name: '',
    end: false,
    approved: false
};

const StageCard = ({ stage, theme, onEdit, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <Paper
            ref={setNodeRef}
            style={style}
            sx={{
                minWidth: 260,
                borderRadius: '22px',
                p: 2.5,
                background: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc',
                border: '2px dashed rgba(37,99,235,0.2)'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}
            >
                <Chip
                    label={stage.name}
                    sx={{
                        background: '#2563eb',
                        color: '#fff',
                        fontWeight: 700
                    }}
                />

                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: 'grab',
                        display: 'flex',
                        color: 'text.secondary'
                    }}
                    title="Arraste para reordenar"
                >
                    <IconArrowsMove size={18} />
                </Box>
            </Box>

            <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                {stage.end && <Chip size="small" label="Gera Venda" />}
                {stage.approved && <Chip size="small" label="Aprovado" />}
            </Stack>

            <Stack direction="row" spacing={1}>
                <Button
                    fullWidth
                    startIcon={<IconEdit size={16} />}
                    sx={{ borderRadius: '14px', textTransform: 'none' }}
                    onClick={() => onEdit(stage)}
                >
                    Editar
                </Button>

                <Button color="error" sx={{ borderRadius: '14px', minWidth: 0, px: 1.5 }} onClick={() => onDelete(stage)}>
                    <IconTrash size={16} />
                </Button>
            </Stack>
        </Paper>
    );
};

const BusinessAdmin = () => {
    const theme = useTheme();

    const [appId, setAppId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pipelines, setPipelines] = useState([]);
    const [teams, setTeams] = useState([]);
    const [search, setSearch] = useState('');

    const [status, setStatus] = useState({ type: '', message: '' });

    const [openPipelineModal, setOpenPipelineModal] = useState(false);
    const [pipelineModalMode, setPipelineModalMode] = useState('create');
    const [editingPipelineId, setEditingPipelineId] = useState(null);
    const [pipelineForm, setPipelineForm] = useState(DEFAULT_PIPELINE);

    const [openStageModal, setOpenStageModal] = useState(false);
    const [stageModalMode, setStageModalMode] = useState('create');
    const [activePipelineId, setActivePipelineId] = useState(null);
    const [editingStageId, setEditingStageId] = useState(null);
    const [stageForm, setStageForm] = useState(DEFAULT_STAGE);

    const history = useHistory();

    useEffect(() => {
        pegarAppPadrao();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadData();
    }, [appId]);

    const pegarAppPadrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar app.' });
        }
    };

    const loadData = async () => {
        setLoading(true);

        try {
            const [pipelinesResponse, teamsResponse] = await Promise.all([
                api.get(`v1/apps/${appId}/pipelines/`),
                api.get(`v1/apps/${appId}/teams/`)
            ]);

            setPipelines(Array.isArray(pipelinesResponse.data) ? pipelinesResponse.data : []);
            setTeams(teamsResponse.data?.teams || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar pipelines.' });
        } finally {
            setLoading(false);
        }
    };

    const filteredPipelines = useMemo(() => {
        return pipelines.filter((pipeline) => pipeline.name?.toLowerCase().includes(search.toLowerCase()));
    }, [pipelines, search]);

    const teamName = (teamId) => teams.find((team) => team.id === teamId)?.name;

    // ------------------------- PIPELINE -------------------------

    const openCreatePipelineModal = () => {
        setPipelineModalMode('create');
        setEditingPipelineId(null);
        setPipelineForm(DEFAULT_PIPELINE);
        setOpenPipelineModal(true);
    };

    const openEditPipelineModal = (pipeline) => {
        setPipelineModalMode('edit');
        setEditingPipelineId(pipeline.id);
        setPipelineForm({
            name: pipeline.name || '',
            teams_id: pipeline.teams || ''
        });
        setOpenPipelineModal(true);
    };

    const handleSavePipeline = async () => {
        if (!pipelineForm.name.trim()) {
            setStatus({ type: 'error', message: 'Informe o nome da pipeline.' });
            return;
        }

        const payload = {
            name: pipelineForm.name,
            teams_id: pipelineForm.teams_id || null
        };

        try {
            if (pipelineModalMode === 'edit') {
                await api.patch(`v1/apps/${appId}/pipelines/${editingPipelineId}/`, payload);
                setStatus({ type: 'success', message: 'Pipeline atualizada com sucesso!' });
            } else {
                await api.post(`v1/apps/${appId}/pipelines/`, payload);
                setStatus({ type: 'success', message: 'Pipeline criada com sucesso!' });
            }

            setOpenPipelineModal(false);
            loadData();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.detail || 'Erro ao salvar pipeline.' });
        }
    };

    const handleDeletePipeline = async (pipeline) => {
        const confirmDelete = window.confirm(`Deseja excluir a pipeline "${pipeline.name}"?`);
        if (!confirmDelete) return;

        try {
            await api.delete(`v1/apps/${appId}/pipelines/${pipeline.id}/`);
            setStatus({ type: 'success', message: 'Pipeline removida.' });
            setPipelines((prev) => prev.filter((item) => item.id !== pipeline.id));
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir pipeline.' });
        }
    };

    // ------------------------- STAGE -------------------------

    const openCreateStageModal = (pipelineId) => {
        setStageModalMode('create');
        setActivePipelineId(pipelineId);
        setEditingStageId(null);
        setStageForm(DEFAULT_STAGE);
        setOpenStageModal(true);
    };

    const openEditStageModal = (pipelineId, stage) => {
        setStageModalMode('edit');
        setActivePipelineId(pipelineId);
        setEditingStageId(stage.id);
        setStageForm({
            name: stage.name || '',
            end: Boolean(stage.end),
            approved: Boolean(stage.approved)
        });
        setOpenStageModal(true);
    };

    const handleSaveStage = async () => {
        if (!stageForm.name.trim()) {
            setStatus({ type: 'error', message: 'Informe o nome do stage.' });
            return;
        }

        try {
            if (stageModalMode === 'edit') {
                await api.patch(`v1/apps/${appId}/pipelines/${activePipelineId}/stages/${editingStageId}/`, {
                    name: stageForm.name,
                    end: stageForm.end,
                    approved: stageForm.approved
                });
                setStatus({ type: 'success', message: 'Stage atualizado com sucesso!' });
            } else {
                const pipeline = pipelines.find((item) => item.id === activePipelineId);
                const ordering = pipeline?.stages?.length || 0;

                await api.post(`v1/apps/${appId}/pipelines/${activePipelineId}/stages/`, {
                    name: stageForm.name,
                    ordering,
                    end: stageForm.end,
                    approved: stageForm.approved
                });
                setStatus({ type: 'success', message: 'Stage criado com sucesso!' });
            }

            setOpenStageModal(false);
            loadData();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao salvar stage.' });
        }
    };

    const handleDeleteStage = async (pipelineId, stage) => {
        const confirmDelete = window.confirm(`Deseja excluir o stage "${stage.name}"?`);
        if (!confirmDelete) return;

        try {
            await api.delete(`v1/apps/${appId}/pipelines/${pipelineId}/stages/${stage.id}/`);
            setStatus({ type: 'success', message: 'Stage removido.' });
            loadData();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao excluir stage.' });
        }
    };

    const handleStageDragEnd = async (pipeline, event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const stages = pipeline.stages || [];
        const oldIndex = stages.findIndex((stage) => stage.id === active.id);
        const newIndex = stages.findIndex((stage) => stage.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;

        const reordered = [...stages];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);

        const reorderedWithIndex = reordered.map((stage, index) => ({ ...stage, ordering: index }));

        // atualização otimista da ordem local
        setPipelines((prev) => prev.map((item) => (item.id === pipeline.id ? { ...item, stages: reorderedWithIndex } : item)));

        const changedStages = reorderedWithIndex.filter((stage, index) => stages[index]?.id !== stage.id);

        try {
            await Promise.all(
                changedStages.map((stage) =>
                    api.patch(`v1/apps/${appId}/pipelines/${pipeline.id}/stages/${stage.id}/`, { ordering: stage.ordering })
                )
            );
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao reordenar stages.' });
            loadData();
        }
    };

    return (
        <Box>
            {/* HERO */}
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '34px',
                    marginTop: 2,
                    p: {
                        xs: 3,
                        md: 5
                    },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)'
                            : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 340,
                        height: 340,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.16))',
                        top: -120,
                        right: -100,
                        filter: 'blur(60px)'
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 2 }}>
                    <Chip icon={<IconSparkles size={14} />} label="Administrador de Negócios" sx={{ mb: 2, fontWeight: 700 }} />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: { xs: '2rem', md: '3rem' },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Pipelines & Negócios
                    </Typography>

                    <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 760, lineHeight: 1.8, mb: 4 }}>
                        Gerencie pipelines e stages do seu funil de vendas. Arraste os stages para reordenar.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                            <OutlinedInput
                                fullWidth
                                placeholder="Buscar pipeline..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                }
                                sx={{
                                    height: 58,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={7}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<IconPlus size={18} />}
                                onClick={openCreatePipelineModal}
                                sx={{
                                    height: 58,
                                    borderRadius: '18px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                                }}
                            >
                                Nova Pipeline
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 3, borderRadius: '16px' }}>
                    {status.message}
                </Alert>
            )}

            {/* PIPELINES */}

            <Typography variant="h4" sx={{ fontWeight: 800, mb: 3 }}>
                Pipelines
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredPipelines.map((pipeline, index) => (
                        <Grid item xs={12} key={pipeline.id}>
                            <MotionCard
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.03 }}
                                sx={{
                                    borderRadius: '28px',
                                    overflow: 'hidden',
                                    border:
                                        theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255,255,255,0.06)'
                                            : '1px solid rgba(15,23,42,0.06)',
                                    background: theme.palette.mode === 'dark' ? '#111827' : '#fff'
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 4,
                                            flexWrap: 'wrap',
                                            gap: 2
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box
                                                sx={{
                                                    width: 68,
                                                    height: 68,
                                                    borderRadius: '20px',
                                                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <IconColumns3 size={32} color="#fff" />
                                            </Box>

                                            <Box>
                                                <Typography variant="h4" sx={{ fontWeight: 900 }}>
                                                    {pipeline.name}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Stack direction="row" spacing={1}>
                                            {teamName(pipeline.teams) && (
                                                <Chip
                                                    icon={<IconUsersGroup size={14} />}
                                                    label={teamName(pipeline.teams)}
                                                    sx={{ fontWeight: 700 }}
                                                />
                                            )}

                                            <Button
                                                startIcon={<IconEdit size={16} />}
                                                onClick={() => openEditPipelineModal(pipeline)}
                                                sx={{ borderRadius: '14px', textTransform: 'none' }}
                                            >
                                                Editar
                                            </Button>

                                            <Button
                                                color="error"
                                                startIcon={<IconTrash size={16} />}
                                                onClick={() => handleDeletePipeline(pipeline)}
                                                sx={{ borderRadius: '14px', textTransform: 'none' }}
                                            >
                                                Excluir
                                            </Button>
                                        </Stack>
                                    </Box>

                                    {/* STAGES */}

                                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                                        Stages da Pipeline
                                    </Typography>

                                    <DndContext
                                        collisionDetection={closestCenter}
                                        onDragEnd={(event) => handleStageDragEnd(pipeline, event)}
                                    >
                                        <SortableContext
                                            items={(pipeline.stages || []).map((stage) => stage.id)}
                                            strategy={horizontalListSortingStrategy}
                                        >
                                            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
                                                {(pipeline.stages || []).map((stage) => (
                                                    <StageCard
                                                        key={stage.id}
                                                        stage={stage}
                                                        theme={theme}
                                                        onEdit={(item) => openEditStageModal(pipeline.id, item)}
                                                        onDelete={(item) => handleDeleteStage(pipeline.id, item)}
                                                    />
                                                ))}

                                                {/* ADD STAGE */}

                                                <Paper
                                                    sx={{
                                                        minWidth: 200,
                                                        borderRadius: '22px',
                                                        p: 2.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border:
                                                            theme.palette.mode === 'dark'
                                                                ? '2px dashed rgba(255,255,255,0.12)'
                                                                : '2px dashed rgba(15,23,42,0.12)',
                                                        background: 'transparent'
                                                    }}
                                                >
                                                    <Button
                                                        startIcon={<IconPlus size={18} />}
                                                        onClick={() => openCreateStageModal(pipeline.id)}
                                                        sx={{ borderRadius: '14px', textTransform: 'none' }}
                                                    >
                                                        Novo Stage
                                                    </Button>
                                                </Paper>
                                            </Box>
                                        </SortableContext>
                                    </DndContext>
                                </CardContent>
                            </MotionCard>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* CREATE / EDIT PIPELINE */}

            <Dialog
                open={openPipelineModal}
                onClose={() => setOpenPipelineModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '28px' } }}
            >
                <DialogTitle>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                        {pipelineModalMode === 'edit' ? 'Editar Pipeline' : 'Nova Pipeline'}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nome da pipeline"
                                value={pipelineForm.name}
                                onChange={(e) => setPipelineForm({ ...pipelineForm, name: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Equipe"
                                value={pipelineForm.teams_id}
                                onChange={(e) => setPipelineForm({ ...pipelineForm, teams_id: e.target.value })}
                            >
                                <MenuItem value="">Sem equipe</MenuItem>
                                {teams.map((team) => (
                                    <MenuItem key={team.id} value={team.id}>
                                        {team.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={18} />}
                        onClick={handleSavePipeline}
                        sx={{
                            borderRadius: '16px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        {pipelineModalMode === 'edit' ? 'Salvar Pipeline' : 'Criar Pipeline'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CREATE / EDIT STAGE */}

            <Dialog
                open={openStageModal}
                onClose={() => setOpenStageModal(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: '28px' } }}
            >
                <DialogTitle>
                    <Typography variant="h4" sx={{ fontWeight: 900 }}>
                        {stageModalMode === 'edit' ? 'Editar Stage' : 'Novo Stage'}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Nome do stage"
                            value={stageForm.name}
                            onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox checked={stageForm.end} onChange={(e) => setStageForm({ ...stageForm, end: e.target.checked })} />
                            }
                            label="Gera Venda (stage final)"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={stageForm.approved}
                                    onChange={(e) => setStageForm({ ...stageForm, approved: e.target.checked })}
                                />
                            }
                            label="Aprovado"
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={18} />}
                        onClick={handleSaveStage}
                        sx={{
                            borderRadius: '16px',
                            px: 3,
                            textTransform: 'none',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                        }}
                    >
                        {stageModalMode === 'edit' ? 'Salvar Stage' : 'Criar Stage'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BusinessAdmin;
